/* Content for the profile page. Kept apart from the markup so the copy can be
   edited without touching layout. */

export const profile = {
  name: 'Jesse Vincent',
  title: 'Lead Software Engineer & Product Driver',
  location: 'Heidelberg, Germany',
  tagline:
    'Linguist turned software engineer. I prototype products, get them in front of users, and scale the ones that stick.',
  about: [
    "I've spent 8 years building and scaling products as a full-stack utility player — the person who leads hands-on development, drives adoption, and pulls in whatever's needed (LLMs, agents, heavy automation) to get a thing off the ground.",
    'I came to engineering through language. A linguistics degree, a TESOL certificate, years of Malay translation and interpreting, and a few semesters teaching text analysis to humanities students. That background is the reason I like the messy problems: reconciling thirty payment gateways that disagree about reality is, structurally, a translation problem.',
  ],
  links: [
    { label: 'Email', href: 'mailto:jesse@langcharm.com', primary: true },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/in/jesse~vincent/' },
    { label: 'LangCharm', href: 'https://langcharm.com' },
  ],
};

export const engineering = [
  {
    period: 'Mar 2026 — Present',
    role: 'Agentic AI Engineer',
    company: 'RLDatix',
    current: true,
    bullets: ['Building agentic AI systems inside healthcare risk and compliance software.'],
  },
  {
    period: 'May 2025 — Feb 2026',
    role: 'Senior Software Engineer',
    company: 'Truelist',
    bullets: [
      'Architected an AI-powered voice and text assistant (Python/FastAPI) that let real estate agents manage property portfolios and automate communication with service providers.',
      'Built an automated pipeline collecting daily traffic analytics, market trends, and listing activity, powering a data-driven dashboard.',
      'Led growth marketing campaigns to drive adoption, delivering sample analytics that help agents reduce time-to-sale.',
    ],
  },
  {
    period: 'Aug 2024 — May 2025',
    role: 'Lead Software Engineer',
    company: 'Nexio',
    bullets: [
      'Led a distributed team of 11 engineers (7 offshore, 4 onsite), owning architecture and technical direction for a payment and invoicing platform.',
      'Built automated invoicing and payment-tracking workflows using ETL pipelines to reconcile external payment state without directly handling funds.',
    ],
  },
  {
    period: 'Jun 2023 — Aug 2024',
    role: 'Senior Software Engineer',
    company: 'Nexio',
    bullets: [
      'Designed a bidirectional payment-sync architecture across ~30 external gateways (ACH/credit card), preventing duplicate data and keeping heterogeneous schemas consistent.',
      'Developed a self-healing AI web scraping feature (Puppeteer) that detects HTML changes automatically, cutting breaking changes and maintenance overhead.',
      'Maintained and scaled PCI-compliant payment microservices, integrating gateways including Stripe and NMI.',
    ],
  },
  {
    period: 'Jan 2019 — Jun 2023',
    role: 'Software Developer',
    company: 'WordCruncher / Brigham Young University',
    bullets: [
      'Led a team of 4 developers through the full lifecycle of a web and mobile text-analysis application (C++/Swift/Python).',
      'Directed development of the corpus analysis software WordCruncher, used by 25,000+ researchers.',
      'Built TextElixir, an NLP tool for searching and analysing large datasets, plus web and application solutions for professors across campus.',
    ],
  },
];

/* The linguistics and teaching work the engineering path grew out of. */
export const roots = [
  {
    period: '2015 — Present',
    role: 'English–Malay Translator & Interpreter',
    company: 'Freelance',
    note: 'Translated websites, magazines, and manuals. Coordinated a team of 15 interpreters for live worldwide conferences.',
  },
  {
    period: '2019 — 2023',
    role: 'Digital Humanities Instructor',
    company: 'Brigham Young University',
    note: 'Taught Tools for Text Processing and Analysis (DIGHT 260) for 5 years. This teaches students how to web scrape using Python, normalize textual data using NLP tools, and visualize text-based data using programs like WordCruncher and AntConc.',
  },
  {
    period: '2018 — 2019',
    role: 'ESL Teacher',
    company: 'English Language Center',
    note: 'Taught a listening & speaking class and a writing class to international students preparing for studying at US universities.',
  },
  {
    period: '2017 — 2018',
    role: 'Linguistic Database Content Analyst',
    company: 'Missionary Training Center',
    note: 'Maintained a language-agnostic index of 22,000 words and phrases across 60+ languages for Embark. Imagine having to figure out how we can map language differences like 5 different ways to say "can" or 300 different ways to say "sausage".',
    noteLink: { text: 'Embark', href: 'https://apps.apple.com/us/app/tall-embark/id6502469985' },
  },
  {
    period: '2017 — 2018',
    role: 'Semantics Teaching Assistant',
    company: 'Brigham Young University',
    note: 'Tutored students, gave lectures, and coached research papers in an introductory semantics course. This is where my evil villain persona comes in and tricks the protagonists by saying, "Semantics, darling."',
  },
];

export const education = {
  school: 'Brigham Young University',
  degree: 'BA Linguistics — Computer Science & Information Systems emphasis',
  extra: 'TESOL Certificate',
  period: '2019',
};

export const skills = [
  { group: 'Frontend', items: ['React', 'TypeScript', 'JavaScript'] },
  { group: 'Backend', items: ['NodeJS (NestJS, Express)', 'Python (FastAPI, Django)', 'C# (.NET)'] },
  { group: 'Cloud & Data', items: ['AWS', 'Serverless', 'Terraform', 'Postgres', 'DynamoDB'] },
  { group: 'Testing & Automation', items: ['Jest', 'Pytest', 'Playwright', 'Puppeteer', 'Selenium'] },
];

/* ---- Language timeline ----
   A language with a `story` becomes clickable in the chart and opens a popover.
   Adding one to any entry below is all it takes — no layout changes.
   `start` / `end` are decimal years. `end: null` means still going. The chart
   maps them onto AXIS_START..AXIS_END, so adding a language needs no layout
   changes. */
export const AXIS_START = 1995;
export const AXIS_END = 2027;
export const AXIS_TICKS = [1995, 2000, 2005, 2010, 2015, 2020, 2025];
export const NOW_YEAR = 2026.75;

export const languages = [
  {
    name: 'English',
    story: 'It all started on the day I was born, or maybe even before that.',
    level: 'Native',
    start: 1995,
    end: null,
    years: '1995 — now',
    color: '#7c3aed',
    dark: true,
  },
  {
    name: 'Spanish',
    story: 'I started learning Spanish in high school, but after a year I quickly went through TV shows and books to learn more than the American ciriculuum offered. This is the language that kicked off my passion for language learning. This is the language I use the most outside of English and German because of friends and family that speak Spanish.',
    level: 'B2',
    start: 2009,
    end: null,
    years: '2009 — now',
    color: '#f59e0b',
  },
  {
    name: 'French',
    story:
      "I took French in high school for a couple years, but have never needed to speak to anyone in French. After visiting Paris once, I realized I would never need this language for obvious reasons.",
    level: 'A2',
    start: 2011,
    end: 2012,
    years: '2011 — 2012',
    color: '#60a5fa',
  },
  {
    name: 'Czech',
    story:
      "I had a girlfriend from the Czech Republic in high school. I learned what I could while we were together and never pursued it past that.",
    level: 'A2',
    start: 2012,
    end: 2013,
    years: '2012 — 2013',
    color: '#f472b6',
  },
  {
    name: 'Malay',
    level: 'C2 · Fluent',
    start: 2013,
    end: null,
    years: '2013 — now',
    color: '#22c55e',
  },
  {
    name: 'Iban',
    level: 'Fluent',
    start: 2014,
    end: null,
    years: '2014 — now',
    color: '#14b8a6',
  },
  {
    name: 'Mongolian',
    story:
      "My one Mongolian friend, invited me to her wedding in Mongolia, so I booked flights to go! And then she cancelled it but told me to still go and spend time with her family, who didn't speak any English. So I frantically learned everything I could so I could survive a week in Mongolia. Honestly such an awesome language, and I hope to have time in the future to learn it more.",
    level: 'A1',
    start: 2018,
    end: 2018.4,
    years: '2018',
    color: '#fb923c',
  },
  {
    name: 'Japanese',
    story:
      "I took tutoring from a friend because I was planning to teach English in Japan for a year, but COVID and other career opportunities steered me into the direction of software development (probably one of my greedier decisions since it pays better, but I'm glad I missed COVID in Japan).",
    level: 'A1',
    start: 2019,
    end: 2019.5,
    years: '2019',
    color: '#ef4444',
    dark: true,
  },
  {
    name: 'German',
    level: 'B2',
    start: 2019,
    end: null,
    years: '2019 — now',
    color: '#a3e635',
  },
];

export const videos = [
  {
    id: 'Y1wgGOaplFQ',
    title: 'San Francisco 2026, Part 1',
    note: 'A few days in the city.',
  },
  {
    id: 'hM8yRR4HOUM',
    title: 'Asia 2018 — Malaysia, Thailand, South Korea',
    note: 'Back through Malaysia, then onward.',
  },
];

/* Full-width cards, in page order. Spire Wizard links in-app via `to`; the rest
   link out via `href`. `featured` gives a card the solid primary button. */
export const passionProjects = [
  {
    name: 'LangCharm',
    note:
      'Language learning built around reading real material, with a dictionary that resolves a word to its lemma so a lookup takes one tap instead of a detour.',
    href: 'https://langcharm.com',
    featured: true,
  },
  {
    name: 'Spire Wizard',
    kicker: 'Live on this site',
    note:
      "A probability-based solver for The Spire diplomacy minigame in Elvenar. It's like Wordle with five open slots — except instead of guessing words, you're guessing which resources the ghosts want.",
    to: '/spire-wizard',
  },
  {
    name: 'SideQuest',
    note: 'An app to plan out your trip and find the best transit between each place.',
    status: 'Work in progress',
    href: 'https://sidequest.javascriptorian.com',
  },
];

export const openSource = [
  {
    name: 'WordCruncher',
    note: 'Corpus analysis software for text research, used by 25,000+ researchers. I led its development for four years.',
    href: 'https://wordcruncher.com',
    hrefLabel: 'wordcruncher.com',
  },
  {
    name: 'TextElixir',
    note: 'A Python toolkit for corpus work — collocates, n-grams, KWIC lines and more, over large text datasets.',
    href: 'https://pypi.org/project/textelixir/',
    hrefLabel: 'PyPI',
  },
  {
    name: 'WebMage',
    note: 'A Python library that makes scraping dynamically loading pages easier.',
    status: 'Dormant',
    href: 'https://pypi.org/project/webmage/',
    hrefLabel: 'PyPI',
  },
];
